import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { z } from "zod";
import { getSafeErrorMessage, logError } from "@/lib/error-handler";

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(128, "Password must be less than 128 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number");

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultView?: "login" | "signup";
}

export function AuthDialog({ open, onOpenChange, defaultView = "login" }: AuthDialogProps) {
  const [isLogin, setIsLogin] = useState(defaultView === "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate password strength on signup
      if (!isLogin) {
        const validation = passwordSchema.safeParse(password);
        if (!validation.success) {
          toast.error(validation.error.errors[0].message);
          setLoading(false);
          return;
        }
      }

      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        toast.success("Welcome back!");
        onOpenChange(false);
        navigate("/dashboard");
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
            },
            emailRedirectTo: `${window.location.origin}/dashboard`,
          },
        });

        if (error) throw error;

        toast.success("Account created! Check your email for verification.");
        setEmail("");
        setPassword("");
        setFullName("");
        setIsLogin(true);
      }
    } catch (error: any) {
      logError("Auth", error);
      toast.error(getSafeErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md glass-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl gradient-text">
            {isLogin ? "Welcome Back" : "Create Account"}
          </DialogTitle>
          <DialogDescription>
            {isLogin
              ? "Sign in to continue to your projects"
              : "Start transforming your designs today"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleAuth} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="fullName" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Full Name
              </Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required={!isLogin}
                autoComplete="off"
                className="glass-input"
                placeholder="John Doe"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete={isLogin ? "email" : "off"}
              className="glass-input"
              placeholder="you@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={isLogin ? "current-password" : "off"}
                className="glass-input pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {!isLogin && (
              <div className="text-xs text-muted-foreground space-y-1 mt-2 pl-1">
                <p className="font-medium text-foreground mb-1.5">Password requirements:</p>
                <ul className="space-y-1">
                  <li
                    className={`flex items-center gap-1.5 ${
                      password.length >= 8 ? "text-green-500" : ""
                    }`}
                  >
                    <span className="w-1 h-1 rounded-full bg-current"></span>
                    At least 8 characters
                  </li>
                  <li
                    className={`flex items-center gap-1.5 ${
                      /[A-Z]/.test(password) ? "text-green-500" : ""
                    }`}
                  >
                    <span className="w-1 h-1 rounded-full bg-current"></span>
                    At least one uppercase letter
                  </li>
                  <li
                    className={`flex items-center gap-1.5 ${
                      /[a-z]/.test(password) ? "text-green-500" : ""
                    }`}
                  >
                    <span className="w-1 h-1 rounded-full bg-current"></span>
                    At least one lowercase letter
                  </li>
                  <li
                    className={`flex items-center gap-1.5 ${
                      /[0-9]/.test(password) ? "text-green-500" : ""
                    }`}
                  >
                    <span className="w-1 h-1 rounded-full bg-current"></span>
                    At least one number
                  </li>
                </ul>
              </div>
            )}
          </div>

          <Button type="submit" className="w-full gradient-button" disabled={loading}>
            {loading ? "Loading..." : isLogin ? "Sign In" : "Create Account"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-primary hover:underline font-medium"
          >
            {isLogin ? "Sign up" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
